import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { DocumentMeta, DocumentMetaDataType } from '@qtek/shared/models';
import { filter, mergeMap, pipe, Subject, takeUntil, tap } from 'rxjs';
import { TermSheetDocMetaService } from './term-sheet-doc-meta.service';

export interface TermSheetConnectPayload {
  mysid: string;
  /** The owning **document** id (not the reference id) — `prms.documentId` on the wire. */
  documentId: string;
  /** Narrows to one or more extractors' output when several may target the same document. */
  dataType?: DocumentMetaDataType | DocumentMetaDataType[];
}

export interface TermSheetState {
  docMeta: Record<string, DocumentMeta>;
}

const initialState: TermSheetState = {
  docMeta: {},
};

export const TermSheetStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, service = inject(TermSheetDocMetaService)) => {
    const disconnect$ = new Subject<string>();

    return {
      // `mergeMap`, not `switchMap`: this store is root-provided/shared, so a
      // second `connect()` (e.g. two dialogs open at once) must not cancel
      // an earlier caller's subscription — each is torn down independently
      // via its own `mysid` through `disconnect`.
      connect: rxMethod<TermSheetConnectPayload>(
        pipe(
          mergeMap(({ mysid, documentId, dataType }) =>
            service
              .subscribeDocMeta({ mysid, prms: { documentId }, s: 'model' })
              .pipe(
                takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tap(({ op, res }: any) => {
                  if ((op !== 'query' && op !== 'ins') || !res) {
                    return;
                  }
                  // v2 model queries return a list; pick the newest match. `documentId` is
                  // filtered here, not just via the subscription params: broadcast fan-out
                  // matches by entity type alone (`TransactionEventReceiver`), so every
                  // `DocumentMeta` row reaches every open subscription regardless of doc.
                  const dataTypes =
                    dataType == null
                      ? null
                      : Array.isArray(dataType)
                        ? dataType
                        : [dataType];
                  const rows: DocumentMeta[] = Array.isArray(res) ? res : [res];
                  const matches = rows.filter(
                    row =>
                      row.document === documentId &&
                      (!dataTypes ||
                        dataTypes.includes(
                          row.dataType as DocumentMetaDataType
                        ))
                  );
                  const latest = matches.at(-1);
                  if (!latest) {
                    return;
                  }
                  patchState(store, state => ({
                    docMeta: { ...state.docMeta, [mysid]: latest },
                  }));
                })
              )
          )
        )
      ),

      disconnect(mysid: string): void {
        disconnect$.next(mysid);
        patchState(store, state => {
          const { [mysid]: _, ...docMeta } = state.docMeta;
          return { docMeta };
        });
      },
    };
  })
);
